// Quality: every open quality alert, in full (frame v2.12, script L939 and
// L954).
//
// The home screen shows the worst few; this is the whole list. An operator
// sees the alerts they raised; a leader on the floor sees every open alert
// about the lines in scope, because that is the list they are answerable
// for. One group, because the other things that used to share this tab have
// places of their own: the andons have the Andon tab, the starved work
// centres go with them (a starved station is a line about to stop for want
// of a pod), and the to-dos live on More under My work. The tab keeps the
// `alerts` key and the /alerts path it always had; only the name and the
// contents moved.

import { useMemo } from 'react';
import { type NeedCardVM, NeedCardList } from '@/components/home/NeedCard';
import { useT } from '@/i18n/useT';
import { useFloorAlerts, useNeedsYou } from '@/queries';
import { qualityNeedCard } from '@/queries/leader';
import { type NeedActions, useOperatorNeedCards } from './HomeScreen';
import { useStore } from '@/store';

export function AlertsScreen({
  floor,
  scopeLabel,
  actions,
}: {
  /** A leader on the floor: the whole plant, not one station. */
  floor: boolean;
  scopeLabel: string;
  actions: NeedActions;
}) {
  const t = useT();
  const wcName = useStore((s) => s.workcentreName);
  const isLeader = useStore((s) => s.isLeader);
  const workcentreId = useStore((s) => s.workcentreId);

  const needs = useNeedsYou();
  const mine = useOperatorNeedCards(needs.items, actions, isLeader, workcentreId).filter((c) => c.key.startsWith('quality:'));
  const floorQuality = useFloorAlerts(floor);
  const floorCards = useMemo<NeedCardVM[]>(
    () => (floorQuality.data ?? []).map((q) => qualityNeedCard(q, t, true, actions.openQuality)),
    [floorQuality.data, t, actions],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <h1 style={{ font: "700 18px 'Space Grotesk',sans-serif", margin: 0 }}>{t.qualityT}</h1>
        <span style={{ font: '500 12px Manrope,sans-serif', color: 'var(--sub)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {floor ? `${t.openOnScopeT} ${scopeLabel}` : `${t.openAtWcT} ${wcName ?? ''}`}
        </span>
      </div>

      {floor ? (
        <Group label={`${t.qualityT} · ${floorCards.length}`} sub={t.openAcrossFloorT} items={floorCards} isPending={floorQuality.isPending} empty={t.noOpenQualityT} />
      ) : (
        <Group label={`${t.qualityT} · ${mine.length}`} sub={t.raisedByYouOpenT} items={mine} isPending={needs.isPending} empty={t.noOpenQualityT} />
      )}
    </div>
  );
}

function Group({
  label,
  sub,
  items,
  isPending,
  empty,
}: {
  label: string;
  sub: string;
  items: NeedCardVM[];
  isPending: boolean;
  empty: string;
}) {
  return (
    <section aria-label={label}>
      <div className="sectionLabel" style={{ marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginBottom: 8 }}>{sub}</div>
      <NeedCardList items={items} isPending={isPending} emptyText={empty} compact />
    </section>
  );
}
